import { useState } from 'react';
import { Alert, Box, Button, Typography } from '@mui/material';
import { CloudUpload as CloudUploadIcon, CheckCircle as CheckCircleIcon } from '@mui/icons-material';
import FilestoreBrowser from '../FilestoreBrowser';
import { FilestoreFile } from '../../api/filestore';

interface ShapefileUploaderProps {
  onUploaded: (filePath: string) => void;
}

export default function ShapefileUploader({ onUploaded }: ShapefileUploaderProps) {
  const [uploadedPath, setUploadedPath] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [filestoreBrowserOpen, setFilestoreBrowserOpen] = useState(false);

  const handleFilestoreFileSelected = (filestoreFile: FilestoreFile) => {
    setUploadedPath(filestoreFile.s3_path);
    setError(null);
    onUploaded(filestoreFile.s3_path);
  };

  return (
    <Box>
      <Typography variant="subtitle2" sx={{ mb: 0.5, fontWeight: 600 }}>
        Upload Shapefile
      </Typography>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
        Upload shapefiles as ZIP files (.zip) or use KML/GeoJSON. The path will be set automatically.
      </Typography>

      <Button
        type="button"
        variant="outlined"
        size="small"
        startIcon={<CloudUploadIcon />}
        onClick={() => setFilestoreBrowserOpen(true)}
      >
        Browse My Files
      </Button>

      {uploadedPath && (
        <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <CheckCircleIcon fontSize="small" color="success" />
          <Typography variant="caption" color="success.main">
            File selected. Path set automatically.
          </Typography>
        </Box>
      )}

      {error && (
        <Alert severity="error" sx={{ mt: 1 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <FilestoreBrowser
        open={filestoreBrowserOpen}
        onClose={() => setFilestoreBrowserOpen(false)}
        onSelectFile={handleFilestoreFileSelected}
        acceptedFileTypes=".zip,.kml,.geojson,.json"
      />
    </Box>
  );
}
